import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { REPORTS_CONFIG_REPORT } from 'routes';
import {
  useLazyGetReportsQuery,
  usePostReportMutation,
} from 'services/newApi/reportsConfigApi';
import {
  ColumnResponse,
  GroupResponse,
  PostReportConfigBody,
} from 'services/types';
import { ReportColumnItem, ReportGroupItem } from 'shared/types/customReports';
import { useActions, useAppSelector } from 'state/store';
import { FormValidation, ReportForm } from '../types';
import { TRADE_ID_GROUP } from '../constants';

type UseReportForm = {
  onSave: () => void;
  onUpdate: () => void;
  onCancel: () => void;

  form: ReportForm;
  setForm: (form: ReportForm) => void;

  isLoading: boolean;

  isModalVisible: boolean;
  setModalVisible: (value: boolean) => void;
  onConfirmModal: () => void;
};

type UseReportFormOptions = {
  initialForm?: ReportForm;
};

export const useReportForm = ({
  initialForm,
}: UseReportFormOptions = {}): UseReportForm => {
  const { setConfirmationModal, setModalRedirect } = useActions();
  const { isExitConfirmationVisible, modalRedirect } = useAppSelector(
    (state) => state.customReportDataState
  );
  const [postReport] = usePostReportMutation();
  const [fetchReports] = useLazyGetReportsQuery();
  const { setSelectedReportId } = useActions();
  const { reports } = useAppSelector((state) => state.customReportDataState);
  const { push } = useHistory();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [columns, setColumns] = useState<ReportColumnItem[]>([]);
  const [groups, setGroups] = useState<ReportGroupItem[]>([TRADE_ID_GROUP]);
  const [validation, setValidation] = useState<Partial<FormValidation>>({});
  const [formDetails, setFormDetails] = useState<ReportForm>({
    name: '',
    description: '',
    type: 'Custom',
    defaultUnit: 'Value',
  });

  useEffect(() => {
    if (initialForm) {
      setFormDetails({ ...formDetails, ...initialForm });
      setColumns(initialForm.columns || []);
      setGroups(initialForm.groups || [TRADE_ID_GROUP]);
      setValidation(initialForm?.validation || {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialForm]);

  const form: ReportForm = useMemo(
    () => ({
      ...formDetails,
      columns,
      groups,
      validation,
    }),
    [columns, formDetails, groups, validation]
  );

  const updateColumns = (updatedColumns: ReportColumnItem[] = []) => {
    const cleaned: ReportColumnItem[] = [];

    updatedColumns.forEach((column) => {
      const alreadyIncluded = cleaned.find((col) => {
        if (col.unit) {
          if (
            column.category === col.category &&
            column.name === col.name &&
            column.unit === col.unit &&
            column.unit !== 'Default Unit'
          ) {
            return col;
          }
        } else if (
          column.category === col.category &&
          column.name === col.name
        ) {
          return col;
        }

        return undefined;
      });

      if (alreadyIncluded === undefined) {
        cleaned.push(column);
      }
    });

    setColumns(cleaned);
  };

  const updateGroups = (list: ReportGroupItem[] = []): void => {
    const cleaned: ReportGroupItem[] = [];

    list.forEach((group) => {
      if (group.name === TRADE_ID_GROUP.name) {
        return;
      }

      const alreadyIncluded = cleaned.find(
        (item) => group.name === item.name && group.category === item.category
      );

      if (alreadyIncluded === undefined) {
        cleaned.push(group);
      }
    });

    setGroups([...cleaned, TRADE_ID_GROUP]);
  };

  const generateFormBody = useCallback(() => {
    const isCustom = form.type === 'Custom';
    const newColumns: ColumnResponse[] = [];
    const newGroups: GroupResponse[] = [];

    if (isCustom) {
      form.columns?.forEach((column, index) =>
        newColumns.push({
          category: column.category,
          name: column.name,
          sort: index,
          unit: column.unit,
        })
      );

      form.groups?.forEach((group, index) =>
        newGroups.push({
          name: group.name,
          category: group.category,
          sort: index,
        })
      );
    }

    const body: PostReportConfigBody = {
      name: form.name as string,
      type: form.type as string,
      description: form.description as string,
      defaultUnit: form.defaultUnit as string,
      columns: newColumns,
      groups: newGroups,
    };

    return body;
  }, [form]);

  const setForm = useCallback(
    (updatedForm: ReportForm) => {
      setFormDetails({ ...formDetails, ...updatedForm });
      updateGroups(updatedForm.groups);
      updateColumns(updatedForm.columns);
      setValidation(updatedForm?.validation || {});
    },
    [formDetails]
  );

  const validateForm = useCallback((): boolean => {
    const formValidation: Partial<FormValidation> = {};

    if (!form.name || form.name === '') {
      formValidation.name = {
        isInvalid: true,
        message: 'Pleaser enter a valid Name.',
      };
    }

    const found = reports
      .filter((rep) => rep.reportConfigurationId !== form.reportConfigurationId)
      .find((rep) => rep.name === form.name);

    if (found) {
      formValidation.name = {
        isInvalid: true,
        message: 'Report name already in use.',
      };
    }

    if (form.type === 'Custom' && form.columns && form.columns.length <= 0) {
      formValidation.columns = {
        isInvalid: true,
        message: 'Please add at least one column.',
      };
    }

    if (Object.keys(formValidation).length > 0) {
      setValidation(formValidation);
      return false;
    }

    return true;
  }, [form, reports]);

  const onSave = useCallback(async () => {
    if (validateForm()) {
      setIsLoading(true);
      const reportBody = generateFormBody();

      const { data, error } = await postReport(reportBody).then((r) => r);

      setIsLoading(false);

      if (!error) {
        setSelectedReportId(data.reportConfigurationId);
        await fetchReports();
        push(REPORTS_CONFIG_REPORT);
      }
    }
  }, [
    validateForm,
    generateFormBody,
    postReport,
    setSelectedReportId,
    fetchReports,
    push,
  ]);

  const onCancel = useCallback(() => {
    push(REPORTS_CONFIG_REPORT);
  }, [push]);

  const onConfirmModal = () => {
    if (modalRedirect) {
      const route = modalRedirect;
      setModalRedirect(undefined);
      setConfirmationModal(false);
      push(route);
      return;
    }

    push(REPORTS_CONFIG_REPORT);
  };

  // Reset modal state on component mount
  useEffect(() => {
    setConfirmationModal(false);
    setModalRedirect(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    onSave,
    onUpdate: onSave,
    onCancel,

    form,
    setForm,

    isLoading,

    isModalVisible: isExitConfirmationVisible,
    setModalVisible: setConfirmationModal,
    onConfirmModal,
  };
};
